import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import {
  Divider,
  Input,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { MoneyIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';

// eslint-disable-next-line max-len
import { GREATER_OR_EQUAL_THAN_ZERO_MESSAGE } from '../../../../ViewUnit/UnitSqFtInformation/UnitSqFtInformationForm/validate';
import { formatCurrency } from '@fortress-technology-solutions/fortress-component-library/utils';
import {
  SetupLabel,
  SetupSwitch,
  SetupTitle,
  SetupValue,
  AnnualRecertMonthPicker,
  SetupMultiSelect,
  SetupDatePicker,
} from '../shared';
import CertificationPastDue from '../../components/CertificationPastDue';
import ProgramPassbookRate from '../ProgramPassbookRate';
import SetupGridItem from '../../../SetupGridItem';
import OnOffTypography from '../../../OnOffTypography';

import type { RDValues } from '../../../types';

// Constants
import messages from './messages';
import messagesAffordableSetup from '../../messages';
import { useFetchMasterRDProjectRentalTypes } from '../../hooks';
import ManagementFee from './ManagementFee';

interface RDFlags {
  passbookRate: boolean;
}

type RDSetupValues = RDValues & {
  flags: RDFlags,
};

type Props = {
  isLoadingPassbookRateData: boolean,
  editMode: boolean,
  onChange: Function,
  values: RDSetupValues,
  propertyState: string,
};

function RDSetup({
  editMode,
  onChange,
  values,
  isLoadingPassbookRateData,
  propertyState,
}: Props) {
  const {
    RDBorrowerId,
    RDProjectName,
    RDProjectNumber,
    RDProjectType,
    RDProgramType,
    RDProjectRentalTypes,
    RDTotalRAUnits,
    RD538Loan,
    isRDRAEnabled,
    passbookRates,
    autoRolloverIncomeAndAssets,
    RDAnnualRecertMonth,
    reserveAccountRequiredBalance,
    loanTransferAmount,
    noteRentPayment,
    icPayment,
    managementFee,
    hotmaConfig,
    RDContractEffectiveDate,
    RDContractExpirationDate,
    RDNYStateContractNumber,
    flags: { passbookRate = false },
    certificationPastDueDefinition,
  } = values;

  const { data: rentalTypeOptions, isLoading: isRentalTypesLoading } =
    useFetchMasterRDProjectRentalTypes();

  const handleRDAnnualRecertMonthChange = (date: Object, validate: Object) => {
    onChange({
      target: { name: 'setup.RDAnnualRecertMonth', value: date },
    });
  };

  const isInvalidCurrency = (value) => {
    const num = Number(value);
    return isNaN(num) || num < 0;
  };

  const handleCurrencyChange = (name, raw) => {
    const sanitized = raw.replace(/[^\d.-]/g, '');
    onChange({ target: { name: `setup.${name}`, value: sanitized } });
  };

  const handleCurrencyBlur = (name, raw) => {
    const formatted = Number(raw || 0).toFixed(2);
    onChange({ target: { name: `setup.${name}`, value: formatted } });
  };

  // Validate contract dates
  const isExpirationBeforeEffective = useMemo(() => {
    if (!RDContractEffectiveDate || !RDContractExpirationDate) return false;
    return moment(RDContractExpirationDate).isBefore(
      moment(RDContractEffectiveDate),
    );
  }, [RDContractEffectiveDate, RDContractExpirationDate]);

  const renderCurrencyInput = (name, value) => {
    const isInvalid = isInvalidCurrency(value);

    return (
      <Input
        id={`${name}-input`}
        name={`setup.${name}`}
        value={value ?? ''}
        onChange={(e) => handleCurrencyChange(name, e.target.value)}
        onBlur={(e) => handleCurrencyBlur(name, e.target.value)}
        startAdornment={<MoneyIcon />}
        type="text"
        error={isInvalid}
        helperText={
          isInvalid ? (
            <Typography variant="bodyMedium" color="error">
              ${GREATER_OR_EQUAL_THAN_ZERO_MESSAGE}
            </Typography>
          ) : null
        }
      />
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        <SetupTitle>
          <FormattedMessage {...messages.RDSetupTitle} />
        </SetupTitle>
        {propertyState?.endsWith('NY') && (
          <Grid item xs={12} sm={6}>
            <SetupLabel {...messages.RDNYContractNumber} />
            <SetupValue
              editMode={editMode}
              name="setup.RDNYStateContractNumber"
              value={RDNYStateContractNumber}
              onChange={onChange}
              placeholder=""
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.RDProgramType} />
          <SetupValue
            editMode={editMode}
            name="setup.RDProgramType"
            value={RDProgramType?.description}
            placeholder=""
            editable={false}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.RDBorrowerId} />
          <SetupValue
            editMode={editMode}
            name="setup.RDBorrowerId"
            value={RDBorrowerId || ''}
            onChange={onChange}
            placeholder=""
            maxLength={255}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.contractEffectiveDate} />
          <SetupDatePicker
            editMode={editMode}
            name="setup.RDContractEffectiveDate"
            value={RDContractEffectiveDate}
            onChange={onChange}
            id="rd-contract-effective-date"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.contractExpirationDate} />
          <SetupDatePicker
            editMode={editMode}
            name="setup.RDContractExpirationDate"
            value={RDContractExpirationDate}
            onChange={onChange}
            id="rd-contract-expiration-date"
            error={isExpirationBeforeEffective}
            helperText={
              isExpirationBeforeEffective ? (
                <Typography variant="bodyMedium" color="error">
                  <FormattedMessage
                    {...messages.expirationBeforeEffectiveDate}
                  />
                </Typography>
              ) : null
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.RDProjectName} />
          <SetupValue
            editMode={editMode}
            name="setup.RDProjectName"
            value={RDProjectName || ''}
            onChange={onChange}
            placeholder=""
            maxLength={255}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.RDRentalAssistance} />
          <SetupSwitch
            switchId="rd-rental-assistance-switch"
            editMode={editMode}
            name="setup.isRDRAEnabled"
            value={isRDRAEnabled}
            onChange={onChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.RDProjectNumber} />
          <SetupValue
            editMode={editMode}
            name="setup.RDProjectNumber"
            value={RDProjectNumber}
            onChange={onChange}
            placeholder=""
            maxLength={255}
          />
        </Grid>
        {isRDRAEnabled && (
          <Grid item xs={12} sm={6}>
            <SetupLabel {...messages.RDTotalRAUnits} />
            <SetupValue
              editMode={editMode}
              name="setup.RDTotalRAUnits"
              value={RDTotalRAUnits}
              onChange={onChange}
              required
              type="number"
              validateFunction={(value) =>
                value !== '' && value >= 1 && value <= 999 && value % 1 === 0
              }
              min={1}
              max={9999}
              step={1}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.RDProjectType} />
          <SetupValue
            editMode={editMode}
            name="setup.RDProjectType"
            value={RDProjectType?.description}
            placeholder=""
            editable={false}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.RD538Loan} />
          <SetupSwitch
            switchId="rd-538-loan-switch"
            editMode={editMode}
            name="setup.RD538Loan"
            disabled={!editMode}
            value={RD538Loan}
            onChange={onChange}
            trueValue={<FormattedMessage {...messagesAffordableSetup.yes} />}
            falseValue={<FormattedMessage {...messagesAffordableSetup.no} />}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.loanTransferAmount} />
          {editMode ? (
            renderCurrencyInput('loanTransferAmount', loanTransferAmount)
          ) : (
            <SetupValue
              switchId="loan-transfer-amount"
              name="setup.loanTransferAmount"
              editMode={false}
              value={
                loanTransferAmount != null
                  ? formatCurrency(Number(loanTransferAmount).toFixed(2))
                  : null
              }
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.noteRentPayment} />
          {editMode ? (
            renderCurrencyInput('noteRentPayment', noteRentPayment)
          ) : (
            <SetupValue
              switchId="note-rent-payment"
              name="setup.noteRentPayment"
              editMode={false}
              value={
                noteRentPayment != null
                  ? formatCurrency(Number(noteRentPayment).toFixed(2))
                  : null
              }
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.icPayment} />
          {editMode ? (
            renderCurrencyInput('icPayment', icPayment)
          ) : (
            <SetupValue
              switchId="ic-payment"
              name="setup.icPayment"
              editMode={false}
              value={
                icPayment != null
                  ? formatCurrency(Number(icPayment).toFixed(2))
                  : null
              }
            />
          )}
        </Grid>
        <SetupGridItem
          label={
            <FormattedMessage
              {...messagesAffordableSetup.annualRecertMonth}
              values={{ program: 'RD' }}
            />
          }
        >
          <AnnualRecertMonthPicker
            handleAnnualRecertMonthChange={handleRDAnnualRecertMonthChange}
            editMode={editMode}
            programName={'RD'}
            value={RDAnnualRecertMonth}
          />
        </SetupGridItem>

        <Grid item xs={12} sm={6}>
          <SetupMultiSelect
            editMode={editMode}
            label={<FormattedMessage {...messages.RDProjectRentalTypes} />}
            name="setup.RDProjectRentalTypes"
            options={rentalTypeOptions}
            value={RDProjectRentalTypes}
            changeFunction={onChange}
            placeholder="Select rental types"
            disabled={isRentalTypesLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SetupLabel {...messages.reserveAccountRequiredBalance} />
          {editMode ? (
            renderCurrencyInput(
              'reserveAccountRequiredBalance',
              reserveAccountRequiredBalance,
            )
          ) : (
            <SetupValue
              switchId="reserve-account-required-balance"
              name="setup.reserveAccountRequiredBalance"
              editMode={false}
              value={
                reserveAccountRequiredBalance != null
                  ? formatCurrency(
                      Number(reserveAccountRequiredBalance).toFixed(2),
                    )
                  : null
              }
            />
          )}
        </Grid>
        <SetupGridItem
          label={
            <FormattedMessage
              {...messagesAffordableSetup.autoRolloverIncomeAndAssets}
            />
          }
          TooltipProps={{
            title: (
              <FormattedMessage
                {...messagesAffordableSetup.autoRolloverIncomeAndAssetsDescription}
              />
            ),
          }}
        >
          <SetupSwitch
            switchId={'rd-auto-rollover-income-and-assets-switch'}
            editMode={editMode}
            name="setup.RDAutoRolloverIncomeAndAssets"
            disabled={!editMode}
            value={autoRolloverIncomeAndAssets}
            onChange={onChange}
          />
        </SetupGridItem>

        <Grid item xs={12} sm={12}>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <SetupLabel {...messagesAffordableSetup.hotmaActive} />
              <OnOffTypography on={hotmaConfig?.hasAdoptedHOTMA ?? false}>
                {hotmaConfig?.hasAdoptedHOTMA ?? false ? (
                  <FormattedMessage {...messagesAffordableSetup.on} />
                ) : (
                  <FormattedMessage {...messagesAffordableSetup.off} />
                )}
              </OnOffTypography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <SetupLabel
                {...messagesAffordableSetup.hotmaImplementationDate}
              />
              <SetupValue
                name="RD.HOTMAEffectiveDate"
                value={
                  hotmaConfig?.hasAdoptedHOTMA ?? false
                    ? hotmaConfig?.effectiveDate
                    : '---'
                }
                editable={false}
              />
            </Grid>
            <CertificationPastDue
              editMode={editMode}
              value={certificationPastDueDefinition}
              name="setup.RDCertificationPastDueDefinition"
              onChange={onChange}
            />
          </Grid>
        </Grid>

        {passbookRate && (
          <Grid item xs={12}>
            <ProgramPassbookRate
              key={'RD_PassbookRates'}
              programName={'RD'}
              isLoadingPassbookRateData={isLoadingPassbookRateData}
              passbookRates={passbookRates}
            />
          </Grid>
        )}
      </Grid>
      <Divider sx={{ marginY: 2 }} />
      <ManagementFee
        editMode={editMode}
        onChange={onChange}
        values={managementFee}
      />
    </>
  );
}

export default RDSetup;
